import React from 'react';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Button,
  Grid,
  Chip,
  alpha,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useTheme,
  Tooltip,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  Clear as ClearIcon,
  Receipt as ReceiptIcon,
  ShoppingCart as ShoppingCartIcon,
  Percent as PercentIcon,
  PointOfSale as CashDrawerIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';


// Helper functions to safely get item price and quantity
const getItemPrice = (item) => parseFloat(item.Price || item.SalePrice || 0);
const getItemQuantity = (item) => parseInt(item.Quantity || 1);

function CartPanel({
  cart = [],
  isTvaIncluded = true,
  discount,
  customVATRate,
  vatRates = [],
  onUpdateQuantity,
  onRemoveItem,
  onClearCart,
  onApplyDiscount,
  onRemoveDiscount,
  onApplyVAT,
  onToggleTvaIncluded,
  onOpenSerialNumber,
  onOpenCashDrawer,
  onOpenCheckout,
}) {
  const { t } = useTranslation();
  const theme = useTheme();


  // Calculate totals with proper rounding
  const subtotal = Math.round(cart.reduce((sum, item) => sum + (item.Quantity * item.Price), 0) * 100) / 100;
  const discountAmount = discount?.amount || 0;
  const totalVAT = Math.round(cart.reduce((sum, item) => {
    const itemTotal = item.Quantity * item.Price;
    const itemRatio = subtotal > 0 ? itemTotal / subtotal : 0;
    const itemDiscountedTotal = itemTotal - (discountAmount * itemRatio);
    const vatRate = customVATRate !== null && customVATRate !== undefined ? customVATRate : (item.VAT || 0);
    return sum + ((itemDiscountedTotal * vatRate) / 100);
  }, 0) * 100) / 100;

  const totalInclBeforeDiscount = isTvaIncluded
    ? Math.round(cart.reduce((sum, item) => sum + item.Quantity * (item.PriceInclVAT ?? item.Price), 0) * 100) / 100
    : Math.round(cart.reduce((sum, item) => {
        const vatRate = customVATRate !== null && customVATRate !== undefined ? customVATRate : (item.VAT || 0);
        return sum + item.Quantity * item.Price * (1 + vatRate / 100);
      }, 0) * 100) / 100;
  const totalIncl = Math.round((totalInclBeforeDiscount - discountAmount) * 100) / 100;

  return (
    <>
      <Paper
        elevation={3}
        sx={{
          width: { xs: '100%', md: 460, lg: 480 },
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          bgcolor: '#f8f9fa',
          borderLeft: '1px solid',
          borderColor: 'divider',
        }}
      >
        {/* Header */}
        <Box
          sx={{
            p: 2,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ShoppingCartIcon sx={{ fontSize: 24 }} />
            <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.1rem' }}>
              {t('current_sale') || 'Current Sale'}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip
              label={cart.length}
              size="small"
              sx={{
                bgcolor: 'white',
                color: '#667eea',
                fontWeight: 800,
                fontSize: '0.85rem',
                minWidth: 28,
                height: 28,
              }}
            />
            <IconButton
              size="small"
              onClick={onClearCart}
              disabled={cart.length === 0}
              sx={{
                color: 'white',
                bgcolor: alpha('#fff', 0.15),
                '&:hover': { bgcolor: alpha('#fff', 0.25) },
                '&:disabled': { color: alpha('#fff', 0.3), bgcolor: alpha('#fff', 0.08) },
              }}
            >
              <ClearIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Box>
        </Box>

        {/* Cart Items */}
        <Box
          sx={{
            flex: 1,
            overflowY: 'auto',
            bgcolor: 'white',
            '&::-webkit-scrollbar': { width: '6px' },
            '&::-webkit-scrollbar-track': { bgcolor: alpha('#000', 0.05) },
            '&::-webkit-scrollbar-thumb': {
              bgcolor: alpha('#667eea', 0.3),
              borderRadius: '3px',
              '&:hover': { bgcolor: alpha('#667eea', 0.5) },
            },
          }}
        >
          {cart.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 6, px: 2 }}>
              <ReceiptIcon sx={{ fontSize: 60, color: '#d0d0d0', mb: 1.5 }} />
              <Typography variant="body1" color="text.secondary" sx={{ fontWeight: 500, fontSize: '0.95rem' }}>
                {t('cart_is_empty') || 'Cart is empty'}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontSize: '0.8rem' }}>
                {t('select_products_to_add') || 'Select products to add'}
              </Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table size="small" sx={{ minWidth: '100%' }}>
                <TableHead>
                  <TableRow sx={{ bgcolor: alpha('#667eea', 0.05) }}>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', py: 0.75, px: 1, color: '#2c3e50' }}>
                      {t('product') || 'Product'}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.72rem', py: 0.75, px: 0.5, color: '#2c3e50', width: 55 }}>
                      {t('price') || 'Price'}
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 700, fontSize: '0.72rem', py: 0.75, px: 0.5, color: '#2c3e50', width: 90 }}>
                      {t('qty') || 'Qty'}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.72rem', py: 0.75, px: 0.5, color: '#2c3e50', width: 60 }}>
                      {t('total') || 'Total'}
                    </TableCell>
                    <TableCell align="center" sx={{ width: 56, py: 0.75, px: 0 }} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {cart.map((item) => {
                    const itemPrice = getItemPrice(item);
                    const itemQuantity = getItemQuantity(item);
                    const rowKey = item._rowKey || item.Id;

                    return (
                      <TableRow
                        key={rowKey}
                        sx={{
                          '&:hover': { bgcolor: alpha('#667eea', 0.03) },
                          borderBottom: '1px solid #f0f0f0',
                        }}
                      >
                        {/* Product Name — SN shown below name */}
                        <TableCell sx={{ py: 0.75, px: 1 }}>
                          <Tooltip title={item.Name || 'Unknown'} placement="top" arrow enterDelay={300}>
                            <Box>
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: 600,
                                  fontSize: '0.8rem',
                                  color: '#2c3e50',
                                  lineHeight: 1.2,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  cursor: 'pointer',
                                  '&:hover': { color: '#667eea' },
                                }}
                              >
                                {item.Name && item.Name.length > 30
                                  ? `${item.Name.substring(0, 30)}…`
                                  : item.Name || 'Unknown'}
                              </Typography>
                              {item.SerialNumber && (
                                <Typography
                                  variant="caption"
                                  sx={{
                                    display: 'block',
                                    fontSize: '0.68rem',
                                    color: '#667eea',
                                    fontWeight: 600,
                                    mt: 0.15,
                                    lineHeight: 1.2,
                                  }}
                                >
                                  SN: {item.SerialNumber}
                                </Typography>
                              )}
                            </Box>
                          </Tooltip>
                        </TableCell>

                        <TableCell align="right" sx={{ py: 0.75, px: 0.5 }}>
                          <Typography variant="caption" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                            €{itemPrice.toFixed(2)}
                          </Typography>
                        </TableCell>

                        <TableCell align="center" sx={{ py: 0.75, px: 0.5 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.25 }}>
                            <IconButton
                              size="small"
                              onClick={() => onUpdateQuantity(rowKey, itemQuantity - 1)}
                              sx={{
                                bgcolor: '#ff5252', color: 'white',
                                width: 20, height: 20, minWidth: 20,
                                '&:hover': { bgcolor: '#ff1744' }, p: 0,
                              }}
                            >
                              <RemoveIcon sx={{ fontSize: 11 }} />
                            </IconButton>
                            <Typography variant="body2" sx={{ fontWeight: 800, color: '#667eea', fontSize: '0.75rem', minWidth: 20, textAlign: 'center' }}>
                              {itemQuantity}
                            </Typography>
                            <IconButton
                              size="small"
                              onClick={() => onUpdateQuantity(rowKey, itemQuantity + 1)}
                              sx={{
                                bgcolor: '#4caf50', color: 'white',
                                width: 20, height: 20, minWidth: 20,
                                '&:hover': { bgcolor: '#43a047' }, p: 0,
                              }}
                            >
                              <AddIcon sx={{ fontSize: 11 }} />
                            </IconButton>
                          </Box>
                        </TableCell>

                        <TableCell align="right" sx={{ py: 0.75, px: 0.5 }}>
                          <Typography variant="body2" sx={{ fontWeight: 700, color: '#667eea', fontSize: '0.8rem' }}>
                            €{(itemPrice * itemQuantity).toFixed(2)}
                          </Typography>
                        </TableCell>

                        <TableCell align="center" sx={{ py: 0.75, px: 0, width: 56 }}>
                          <Box sx={{ display: 'flex', gap: 0.25, justifyContent: 'center' }}>
                            {onOpenSerialNumber && (
                              <Tooltip title={t('serial_number') || 'Serial Number'}>
                                <IconButton
                                  size="small"
                                  onClick={() => onOpenSerialNumber(item)}
                                  sx={{
                                    width: 24, height: 24, minWidth: 24,
                                    bgcolor: item.SerialNumber ? alpha('#667eea', 0.15) : alpha('#999', 0.08),
                                    color: item.SerialNumber ? '#667eea' : '#999',
                                    fontSize: '0.65rem', fontWeight: 700,
                                    '&:hover': { bgcolor: alpha('#667eea', 0.25), color: '#667eea' },
                                    p: 0,
                                  }}
                                >
                                  SN
                                </IconButton>
                              </Tooltip>
                            )}
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => onRemoveItem(rowKey)}
                              sx={{ width: 24, height: 24, minWidth: 24, '&:hover': { bgcolor: alpha('#f44336', 0.1) }, p: 0 }}
                            >
                              <DeleteIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>

        {/* Action Buttons Row — TVA, Discount, VAT, Drawer */}
        <Box sx={{ bgcolor: 'white', borderTop: '1px solid #e0e0e0', borderBottom: '2px solid #e0e0e0', px: 1.5, py: 1 }}>
          <Grid container spacing={1} alignItems="center">

            {/* TVA Include */}
            <Grid item>
              <Tooltip title={isTvaIncluded ? (t('price_includes_vat') || 'Price includes TVA') : (t('vat_added_on_top') || 'TVA added on top')}>
                <Box
                  onClick={() => onToggleTvaIncluded && onToggleTvaIncluded(!isTvaIncluded)}
                  sx={{
                    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                    width: 62, height: 62, borderRadius: 2,
                    bgcolor: alpha('#00897b', 0.1),
                    border: '2px solid', borderColor: isTvaIncluded ? '#00897b' : alpha('#00897b', 0.3),
                    cursor: 'pointer', transition: 'all 0.2s', position: 'relative',
                    '&:hover': { bgcolor: alpha('#00897b', 0.2), borderColor: '#00897b', transform: 'translateY(-2px)', boxShadow: '0 4px 8px rgba(0,137,123,0.3)' },
                  }}
                >
                  {isTvaIncluded && (
                    <Chip label="✓" size="small" sx={{ position: 'absolute', top: -8, right: -8, bgcolor: '#4caf50', color: 'white', fontWeight: 700, minWidth: 20, height: 20, '& .MuiChip-label': { px: 0.5 } }} />
                  )}
                  <PercentIcon sx={{ fontSize: 22, color: '#00897b', mb: 0.5 }} />
                  <Typography variant="caption" sx={{ fontSize: '0.6rem', fontWeight: 600, color: '#00897b' }}>
                    {t('tva_incl') || 'TVA Incl'}
                  </Typography>
                </Box>
              </Tooltip>
            </Grid>

            {/* Cash Drawer (disabled) */}
            <Grid item>
              <Tooltip title={t('open_cash_drawer') || 'Open Cash Drawer (unavailable)'}>  
                <Box
                  sx={{
                    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                    width: 62, height: 62, borderRadius: 2,
                    bgcolor: alpha('#9e9e9e', 0.07),
                    border: '2px solid', borderColor: alpha('#9e9e9e', 0.2),
                    cursor: 'not-allowed', opacity: 0.45,
                  }}
                >
                  <CashDrawerIcon sx={{ fontSize: 22, color: '#9e9e9e', mb: 0.5 }} />
                  <Typography variant="caption" sx={{ fontSize: '0.6rem', fontWeight: 600, color: '#9e9e9e' }}>
                    {t('drawer') || 'Drawer'}
                  </Typography>
                </Box>
              </Tooltip>
            </Grid>
          </Grid>
        </Box>

        {/* Total bar + Generate button */}
        <Box sx={{ bgcolor: 'white', boxShadow: '0 -2px 8px rgba(0,0,0,0.08)', flexShrink: 0 }}>
          <Box sx={{ px: 2, py: 1.5, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
            <Typography variant="caption" sx={{ opacity: 0.9, fontSize: '0.7rem' }}>
              {t('subtotal') || 'Subtotal'}: €{subtotal.toFixed(2)}
              {discount && ` − Discount: €${discountAmount.toFixed(2)}`}
              {' + '}{t('vat') || 'VAT'}: €{totalVAT.toFixed(2)}
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 800, fontSize: '1.25rem', mt: 0.25 }}>
              {t('total') || 'TOTAL'}: €{totalIncl.toFixed(2)}
            </Typography>
          </Box>

          <Box sx={{ p: 2, pt: 1.5 }}>
            <Button
              fullWidth
              variant="contained"
              size="large"
              startIcon={<ReceiptIcon fontSize="small" />}
              disabled={cart.length === 0 || totalIncl === 0}
              onClick={() => {
                if (cart.length === 0) {
                  toast.error(t('please_add_products') || 'Please add at least one product');
                  return;
                }
                onOpenCheckout && onOpenCheckout();
              }}
              sx={{
                py: 1.5, fontWeight: 700, fontSize: '1rem', borderRadius: 1.5,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                boxShadow: '0 4px 12px rgba(102,126,234,0.4)',
                '&:hover': { background: 'linear-gradient(135deg, #5568d3 0%, #6a3f8f 100%)', boxShadow: '0 6px 16px rgba(102,126,234,0.5)' },
                '&:disabled': { background: '#e0e0e0', color: '#9e9e9e' },
              }}
            >
              {t('generate') || 'Generate'}
            </Button>
          </Box>
        </Box>
      </Paper>

    </>
  );
}

export default CartPanel;
